import React, { useState } from 'react';
import { X, Calendar, Clock, User, Phone, Mail, Stethoscope } from 'lucide-react';
import { team, timeSlots, services } from '../data/mock';
import { CustomSelect, CustomDatePicker } from './BookingModalComponents';
import { supabase } from '../lib/supabase';
import { sendTelegramNotification } from '../lib/telegram';
import '../styles/booking.css';

const BookingModal = ({ onClose, preSelectedService, preSelectedDoctor }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: preSelectedService || '',
    doctor: preSelectedDoctor || '',
    date: null,
    time: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState(timeSlots);
  const [errors, setErrors] = useState({});
  const isFirstRender = React.useRef(true);
  const hasAnimated = React.useRef(false);

  // Update service and doctor if preSelected values change
  React.useEffect(() => {
    if (preSelectedService) {
      setFormData(prev => ({
        ...prev,
        service: preSelectedService
      }));
    }
  }, [preSelectedService]);

  React.useEffect(() => {
    if (preSelectedDoctor) {
      setFormData(prev => ({
        ...prev,
        doctor: preSelectedDoctor
      }));
    }
  }, [preSelectedDoctor]);

  // Animate modal opening using Web Animations API
  React.useEffect(() => {
    isFirstRender.current = false;
    
    const modalContent = document.querySelector('.modal-content');
    
    if (modalContent && !hasAnimated.current) {
      hasAnimated.current = true;
      
      // Animate content slide up
      modalContent.animate([
        { opacity: 0, transform: 'translate3d(0, 50px, 0) scale(0.95)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
      ], {
        duration: 400,
        easing: 'ease',
        fill: 'forwards'
      });
    }
  }, []);



  // Завантажити заблоковані слоти коли змінюється дата або лікар
  React.useEffect(() => {
    if (formData.date && formData.doctor) {
      loadBlockedSlots();
    } else {
      setAvailableTimeSlots(timeSlots);
    }
  }, [formData.date, formData.doctor]);

  // Ініціалізація при першому завантаженні
  React.useEffect(() => {
    if (formData.date && formData.doctor) {
      loadBlockedSlots();
    }
  }, []);

  const loadBlockedSlots = React.useCallback(async () => {
    try {
      // Використовуємо локальну дату
      const year = formData.date.getFullYear();
      const month = String(formData.date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const doctorInfo = team.find(d => `${d.name} (${d.position})` === formData.doctor);
      if (!doctorInfo) {
        return;
      }
      
      const doctorName = `${doctorInfo.name} (${doctorInfo.position})`;
      
      const { data: blockedData, error: blockedError } = await supabase
        .from('blocked_slots')
        .select('blocked_time')
        .eq('doctor_name', doctorName)
        .eq('blocked_date', dateStr);

      if (blockedError) throw blockedError;
      
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('booking_time')
        .eq('doctor_name', doctorName)
        .eq('booking_date', dateStr)
        .neq('status', 'cancelled');

      if (bookingsError) throw bookingsError;
      
      const blockedTimes = blockedData?.map(slot => slot.blocked_time) || [];
      const bookedTimes = bookingsData?.map(booking => booking.booking_time) || [];
      const allUnavailable = [...new Set([...blockedTimes, ...bookedTimes])];
      
      const available = timeSlots.filter(time => !allUnavailable.includes(time));
      setAvailableTimeSlots(available);
      
      if (formData.time && allUnavailable.includes(formData.time)) {
        setFormData(prev => ({ ...prev, time: '' }));
      }
    } catch (error) {
      console.error('Error loading blocked slots:', error);
    }
  }, [formData.date, formData.doctor, formData.time]);

  const handleChange = React.useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Очистити помилку для цього поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  }, [errors]);

  const handleSelectChange = React.useCallback((field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Очистити помилку для цього поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  }, [errors]);

  const handleDateChange = React.useCallback((date) => {
    setFormData(prev => ({
      ...prev,
      date: date,
      time: '' // Сбрасываем время при изменении даты
    }));
    // Очистити помилку для дати
    if (errors.date) {
      setErrors(prev => ({
        ...prev,
        date: false
      }));
    }
    // Очистити помилку для часу, якщо була
    if (errors.time) {
      setErrors(prev => ({
        ...prev,
        time: false
      }));
    }
  }, [errors]);

  const handleClose = React.useCallback(() => {
    const modalContent = document.querySelector('.modal-content');
    const modalOverlay = document.querySelector('.modal-overlay');
    
    if (modalContent && modalOverlay) {
      // Animate closing using Web Animations API
      modalContent.animate([
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        { opacity: 0, transform: 'translate3d(0, 30px, 0)' }
      ], {
        duration: 300,
        easing: 'ease',
        fill: 'forwards'
      });
      
      modalOverlay.animate([
        { opacity: 1 },
        { opacity: 0 }
      ], {
        duration: 300,
        easing: 'ease',
        fill: 'forwards'
      });
    }
    
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const handleSubmit = React.useCallback(async (e) => {
    e.preventDefault();
    
    // Валідація всіх полів
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!formData.phone) newErrors.phone = true;
    if (!formData.service) newErrors.service = true;
    if (!formData.doctor) newErrors.doctor = true;
    if (!formData.date) newErrors.date = true;
    if (!formData.time) newErrors.time = true;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      // Витягуємо ім'я лікаря з формату "Ім'я (Посада)"
      const doctorName = formData.doctor.split(' (')[0];
      const doctorInfo = team.find(d => d.name === doctorName);
      if (!doctorInfo) {
        alert('Будь ласка, виберіть лікаря зі списку');
        return;
      }

      // Використовуємо локальну дату
      const year = formData.date.getFullYear();
      const month = String(formData.date.getMonth() + 1).padStart(2, '0');
      const day = String(formData.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const bookingData = {
        doctor_name: `${doctorInfo.name} (${doctorInfo.position})`,
        service_title: formData.service,
        patient_name: formData.name,
        patient_phone: formData.phone,
        patient_email: formData.email || null,
        booking_date: dateStr,
        booking_time: formData.time,
        status: 'pending'
      };

      const { data: savedBooking, error } = await supabase
        .from('bookings')
        .insert([bookingData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      sendTelegramNotification(savedBooking).catch(err => {
        console.error('Telegram error:', err);
      });

      setIsSubmitted(true);
      
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Error saving booking:', error);
      alert(`Помилка збереження: ${error.message}`);
    }
  }, [formData, handleClose]);

  const serviceOptions = React.useMemo(() => {
    const allServices = [];
    
    services.forEach(service => {
      if (service.subServices) {
        // Add sub-services
        service.subServices.forEach(subService => {
          allServices.push({
            value: subService.title,
            label: subService.title
          });
        });
      } else {
        // Add main service if no sub-services
        allServices.push({
          value: service.title,
          label: service.title
        });
      }
    });
    
    return allServices;
  }, []);

  const doctorOptions = React.useMemo(() => team.map(doctor => ({
    value: `${doctor.name} (${doctor.position})`,
    label: `Др. ${doctor.name}`
  })), []);

  const timeOptions = React.useMemo(() => availableTimeSlots.map(time => ({
    value: time,
    label: time
  })), [availableTimeSlots]);

  const isTimeDisabled = !formData.date;
  const timePlaceholder = isTimeDisabled ? 'Спочатку оберіть дату' : 'Оберіть час';



  const handleOverlayClick = React.useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={handleClose} type="button">
          <X size={24} />
        </button>
        
        {!isSubmitted ? (
          <>
            <h2 className="modal-title">Запис на прийом</h2>
            <p className="modal-subtitle">Заповніть форму, і ми зв'яжемося з вами</p>
            
            <form className="booking-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <User size={18} />
                    Ім'я *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ваше ім'я"
                    className={errors.name ? 'error' : ''}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <Phone size={18} />
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+380"
                    className={errors.phone ? 'error' : ''}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <Mail size={18} />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <Stethoscope size={18} />
                    Послуга *
                  </label>
                  <CustomSelect
                    options={serviceOptions}
                    value={formData.service}
                    onChange={handleSelectChange('service')}
                    placeholder="Оберіть послугу"
                    icon={<Stethoscope size={18} />}
                    error={errors.service}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <User size={18} />
                    Лікар *
                  </label>
                  <CustomSelect
                    options={doctorOptions}
                    value={formData.doctor}
                    onChange={handleSelectChange('doctor')}
                    placeholder="Оберіть лікаря"
                    icon={<User size={18} />}
                    error={errors.doctor}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <Calendar size={18} />
                    Дата *
                  </label>
                  <div className="date-picker-container">
                    <CustomDatePicker 
                      selectedDate={formData.date} 
                      onChange={handleDateChange}
                      error={errors.date}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <Clock size={18} />
                    Час *
                  </label>
                  <CustomSelect
                    options={timeOptions}
                    value={formData.time}
                    onChange={handleSelectChange('time')}
                    placeholder={timePlaceholder}
                    icon={<Clock size={18} />}
                    error={errors.time}
                    disabled={isTimeDisabled}
                  />
                </div>
              </div>

              <button type="submit" className="submit-btn">
                <Calendar size={20} />
                Підтвердити запис
              </button>
            </form>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Дякуємо!</h2>
            <p>Ваш запис прийнято</p>
            <p className="success-details">
              Ми зв'яжемося з вами найближчим часом для підтвердження
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
